/**
 * QuotationListView Component
 * Table list view for quotations
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Quotation, QuotationType } from '@/types/quotations';
import { isQuotationValid, formatPricingTier, getQuotationPartyCode, getQuotationPartyName } from '@/types/quotations';
import { formatDate } from '@/types/common';

interface QuotationListViewProps {
  quotations: Quotation[];
  type: QuotationType;
  isLoading?: boolean;
  onEdit?: (quotation: Quotation) => void;
  onDelete?: (quotation: Quotation) => void;
  onView?: (quotation: Quotation) => void;
}

export const QuotationListView: React.FC<QuotationListViewProps> = ({
  quotations,
  type,
  isLoading = false,
  onEdit,
  onDelete,
  onView,
}) => {
  const { t } = useTranslation('psi');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (quotations.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {t('quotations.noQuotations')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('quotations.columns.quotationId')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {type === 'purchase' ? t('quotations.columns.vendor') : t('quotations.columns.customer')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('quotations.columns.date')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('quotations.columns.validity')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('quotations.columns.items')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('quotations.columns.status')}
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('common.actions')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {quotations.map((quotation) => {
            const isExpanded = expandedId === quotation.quotation_id;
            const isValid = isQuotationValid(quotation);

            return (
              <React.Fragment key={quotation.quotation_id}>
                {/* Main row */}
                <tr
                  className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  onClick={() => toggleExpand(quotation.quotation_id)}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <button
                        className="mr-2 text-gray-400 hover:text-gray-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(quotation.quotation_id);
                        }}
                      >
                        <svg
                          className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {quotation.quotation_id}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {getQuotationPartyName(quotation) || getQuotationPartyCode(quotation)}
                    </div>
                    {getQuotationPartyName(quotation) && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {getQuotationPartyCode(quotation)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(quotation.date)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(quotation.valid_from)} ~ {formatDate(quotation.valid_until)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                      {quotation.items.length} {t('quotations.itemCount')}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        isValid
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}
                    >
                      {isValid ? t('quotations.status.valid') : t('quotations.status.expired')}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      {onView && (
                        <button
                          onClick={() => onView(quotation)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          {t('common.view')}
                        </button>
                      )}
                      {onEdit && (
                        <button
                          onClick={() => onEdit(quotation)}
                          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                        >
                          {t('common.edit')}
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => onDelete(quotation)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          {t('common.delete')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Expanded items row */}
                {isExpanded && (
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <td colSpan={7} className="px-4 py-3">
                      <div className="pl-8">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                {t('quotations.columns.partCode')}
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                {t('quotations.columns.description')}
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                {t('quotations.columns.pricingTiers')}
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                {t('quotations.columns.leadTime')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {quotation.items.map((item, idx) => (
                              <tr key={idx}>
                                <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                                  {item.part_code}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                  <div>{item.description}</div>
                                  {item.specification && (
                                    <div className="text-xs text-gray-400">{item.specification}</div>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <div className="space-y-1">
                                    {item.pricing_tiers.map((tier, tierIdx) => (
                                      <div
                                        key={tierIdx}
                                        className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded"
                                      >
                                        {formatPricingTier(tier, item.currency || quotation.currency)}
                                      </div>
                                    ))}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                  {item.lead_time_days} {t('quotations.days')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default QuotationListView;
